// Service landing page data. Each entry powers /services/:slug via ServicePage.jsx
// and is also consumed by scripts/prerender.mjs so crawlers get real HTML.
// Related case-study slugs are pulled from src/data/projects.js at render time.

const services = [
  {
    slug: "ai-chatbot-development",
    // Core SEO
    title: "AI Chatbot Development — Custom Chatbots Built for Your Business | Gadget Guy Media",
    metaDescription:
      "Custom AI chatbot development by Gadget Guy Media. Chatbots trained on your business, embedded on your site, with lead capture, analytics, and human handoff — shipped in weeks.",
    keyword: "AI chatbot development",
    // On-page
    eyebrow: "Service",
    h1: "Custom AI Chatbot Development",
    tagline:
      "Chatbots trained on your business. Embedded on your site. Shipped in weeks.",
    intro:
      "Most chatbots on the internet today are either generic support widgets that answer nothing, or ChatGPT wrappers with your logo on top. Neither one moves the needle for a business that has actual customers, actual questions, and actual decisions to make. A custom AI chatbot is different — it's trained on your business, it lives where your customers already are, and it converts curiosity into leads instead of tickets.",
    intro2:
      "Gadget Guy Media builds custom AI chatbots end-to-end. I scope the use case with you, gather the knowledge sources (your site, docs, PDFs, policies, prior support threads), build the chatbot with a modern LLM (Claude, GPT, or Gemini depending on the workload), embed it on your site, and wire up the analytics so you know exactly what's happening. Every chatbot I ship is owned by you — no per-seat lock-in, no monthly rent on your own data, no 'ready next quarter' excuses.",
    // What you get
    whatYouGetHeadline: "What a Gadget Guy Media chatbot includes",
    whatYouGet: [
      {
        title: "Trained on your business",
        body:
          "The bot answers using your product docs, site copy, FAQs, and any structured knowledge you have. Not the generic internet.",
      },
      {
        title: "Embedded, not bolted on",
        body:
          "Ships as a widget on your site or as a standalone chat page — your choice, styled to your brand.",
      },
      {
        title: "Lead capture + analytics",
        body:
          "Every conversation is logged and searchable. Qualified leads get routed to email, CRM, or Slack. You see what people actually ask.",
      },
      {
        title: "Human handoff when it matters",
        body:
          "The bot knows when to escalate. Handoff routes to email, a booked call, or a live channel — configurable per intent.",
      },
      {
        title: "Guardrails + tone control",
        body:
          "System prompt tuned to your voice and your rules. No off-brand replies, no rogue promises, no leaking of internal info.",
      },
      {
        title: "Ownership, not rental",
        body:
          "Once the project is paid in full, you own the final custom code, the prompts, the data, and the vendor relationship. No lock-in, no per-message pricing surprise.",
      },
    ],
    // Process
    processHeadline: "How the build works",
    process: [
      {
        step: "01",
        title: "Scoping call",
        body:
          "45 minutes on Zoom. We talk through what the bot needs to do, who it's for, and what a win looks like. You leave with a clear scope, a clear price, and a target delivery window.",
      },
      {
        step: "02",
        title: "Knowledge + design",
        body:
          "I collect your knowledge sources, draft the system prompt, and design the chat UI to match your site. You approve before build starts.",
      },
      {
        step: "03",
        title: "Build + private demo",
        body:
          "Chatbot built end-to-end (LLM plumbing, embed widget, admin dashboard, analytics). You get a private link to test drive it before it goes live.",
      },
      {
        step: "04",
        title: "Ship + hand-off",
        body:
          "Embed goes live on your site, credentials are handed to you, and I walk you through the admin dashboard. You own everything from day one.",
      },
    ],
    // FAQ
    faqHeadline: "Frequently asked questions about AI chatbot development",
    faqs: [
      {
        q: "How much does a custom AI chatbot cost?",
        a: "Most chatbot builds start in the low-four-figures and scale from there depending on how many knowledge sources, integrations, and intents are in scope. Book a scoping call and I'll give you a real number for your project — not a guess.",
      },
      {
        q: "How long does it take to build a custom AI chatbot?",
        a: "Simple site chatbots (single knowledge source, single intent) ship in 1–2 weeks. Complex bots with multiple integrations, CRM sync, or handoff logic land in 3–5 weeks. If your project can't ship in weeks, I'll tell you upfront.",
      },
      {
        q: "Which LLM do you use — GPT, Claude, or Gemini?",
        a: "Whichever fits the workload best. I'm model-agnostic. Most chatbot work goes to Claude (best natural voice) or GPT-5 (best structured reasoning); some workloads run cheaper on Gemini. The choice is made per project, not per contract.",
      },
      {
        q: "Will the chatbot make things up (hallucinate)?",
        a: "Not on the questions it's trained for. I use retrieval-augmented generation (RAG) so answers come from your actual knowledge sources, and I add refusal logic for out-of-scope questions. The bot says 'I don't know, let me connect you with someone' instead of inventing an answer.",
      },
      {
        q: "Can the chatbot capture leads or book meetings?",
        a: "Yes. Lead capture is standard on every build — email, phone, Calendly booking, or CRM push are all options. The bot can qualify the lead first (budget, timeline, use case) so you're not chasing every drive-by.",
      },
      {
        q: "Do I own the chatbot after you build it?",
        a: "Yes. Once the project is paid in full, you own the final custom code, the prompts, the knowledge base, and the vendor accounts (LLM API keys are set up in your name). No per-seat rent, no vendor lock-in. If you ever want to bring it in-house or hand it to another developer, you can.",
      },
      {
        q: "What does it cost to run the chatbot after launch?",
        a: "Two line items: LLM API usage (typically $10–$150/month depending on chat volume) and hosting (usually $5–$25/month). You pay these providers directly, at cost. No middleman markup.",
      },
    ],
    // Related project slugs — case studies that show off this service
    relatedProjectSlugs: ["ezbuildabot", "glitch", "family-health-care-of-gallatin"],
    // CTA
    ctaHeadline: "Ready to ship a chatbot that actually earns its keep?",
    ctaBody:
      "Book a 45-minute scoping call. You leave with a clear scope, a clear price, a target delivery window, and a clear picture of what your chatbot will do on day one.",
  },

  {
    slug: "ai-app-development",
    title: "Custom AI App Development — Production-Ready AI Apps in Weeks | Gadget Guy Media",
    metaDescription:
      "Custom AI app development by Gadget Guy Media. Production-ready AI apps and internal tools for founders and operators — clear scope, clear price, target delivery window, shipped in weeks not quarters.",
    keyword: "custom AI app development",
    eyebrow: "Service",
    h1: "Custom AI App Development",
    tagline:
      "Production-ready AI apps and internal tools — shipped in weeks, not quarters.",
    intro:
      "Every founder and operator I talk to has the same problem: they see a clear use case where AI would save hours, unlock revenue, or replace a spreadsheet held together with prayer — but the agency quotes come back priced like enterprise projects, with delivery dates months out. Meanwhile the DIY tools (Bubble, Zapier, ChatGPT plugins) leave them staring at a blank canvas or duct-taping something brittle. Neither path actually gets the app shipped.",
    intro2:
      "Custom AI app development at Gadget Guy Media is the third path: a productized delivery model that scopes the app, builds it end-to-end (frontend, backend, database, AI plumbing), and ships a live, production-ready product in weeks — not quarters. Clear scope. Clear price. Target delivery window. Once the project is paid in full, you own the final custom code. That's it. It's the same delivery model that shipped ezScribe (AI medical scribe designed for HIPAA-conscious healthcare workflows), ezItemizer (parts inventory SaaS for manufacturers), Car Value Recovery (structured AI reasoning for diminished-value claims), and every other project on the portfolio.",
    whatYouGetHeadline: "What a Gadget Guy Media custom AI app build includes",
    whatYouGet: [
      {
        title: "Full-stack build, not a prototype",
        body:
          "Modern React frontend + FastAPI or Node backend + MongoDB or Postgres + AI plumbing (Claude, GPT, or Gemini). Production infrastructure, not a Bubble demo.",
      },
      {
        title: "Clear scope, clear price, target delivery window",
        body:
          "You get a real number and a real ship date at the end of the scoping call. No hourly surprises, no scope-creep bait-and-switch.",
      },
      {
        title: "AI-first, not AI-bolted-on",
        body:
          "The AI is designed into the workflow — prompt engineering, retrieval logic, evals, guardrails. Not a ChatGPT button jammed onto a legacy app.",
      },
      {
        title: "You own the code (once paid in full)",
        body:
          "Repo transfers to your GitHub on ship day. No vendor lock-in, no per-seat rent, no black box. Any developer can pick it up.",
      },
      {
        title: "Deployed and live",
        body:
          "Ships on modern hosting (Emergent, Vercel, or your provider of choice) with a real domain, HTTPS, and analytics wired up on day one.",
      },
      {
        title: "One person builds it — with AI as the co-pilot",
        body:
          "No account manager between you and the person writing the code. Faster decisions, tighter scope, no telephone-game requirements loss.",
      },
    ],
    processHeadline: "How the build works",
    process: [
      {
        step: "01",
        title: "Scoping call",
        body:
          "45 minutes on Zoom to talk through the use case, the users, and what 'done' looks like. You leave with a fixed quote and a ship date.",
      },
      {
        step: "02",
        title: "Design + architecture",
        body:
          "I sketch the UI, define the data model, and pick the AI stack. You approve the spec before a single line of code gets written.",
      },
      {
        step: "03",
        title: "Weekly ship + demo",
        body:
          "Working software every week — you see real progress on a live URL, not a Gantt chart. Course-corrections happen in days, not months.",
      },
      {
        step: "04",
        title: "Launch + hand-off",
        body:
          "App goes live on your domain, repo transfers to your GitHub, credentials handed over. I stay on for two weeks of free polish and bug fixes after launch.",
      },
    ],
    faqHeadline: "Frequently asked questions about custom AI app development",
    faqs: [
      {
        q: "How much does a custom AI app build cost?",
        a: "Most custom AI app builds start in the mid-four-figures and scale from there depending on scope — number of features, integrations, and AI workflows. Book a scoping call and I'll give you a real number for your project, not a guess.",
      },
      {
        q: "How long does it take to build a custom AI app?",
        a: "Many builds can ship in a matter of weeks. Your timeline depends on scope, integrations, testing, and complexity. Simple internal tools usually move faster than full SaaS-style apps with auth, billing, and multi-user workflows. If your project can't ship in weeks, I'll tell you upfront and we can either scope it down or you can walk.",
      },
      {
        q: "What kinds of AI apps do you build?",
        a: "Internal tools, customer-facing SaaS, marketplaces, agents, chatbots, document processors, voice interfaces, and workflow automations. Anywhere an LLM adds real leverage, I can build it. Some shipped examples: ezScribe (AI medical scribe designed for HIPAA-conscious healthcare workflows), ezItemizer (parts inventory SaaS), Car Value Recovery (AI-assisted insurance claims), Glitch (private voice AI).",
      },
      {
        q: "Do I own the code, or is it locked to your platform?",
        a: "Once the project is paid in full, you own the final custom code. Full repo transfer to your GitHub on ship day. No vendor lock-in, no per-seat rent, no black-box hosting requirement. Any developer can pick up the codebase and extend it.",
      },
      {
        q: "Do you handle hosting and infrastructure?",
        a: "Yes. Apps ship on modern static + serverless infrastructure (Emergent, Vercel, or your provider of choice). Hosting costs are pass-through at provider rates — you pay them directly, no markup.",
      },
      {
        q: "What if I need changes after launch?",
        a: "Two weeks of free polish and bug fixes are included with every build. Beyond that, ongoing work is available on a per-project basis.",
      },
      {
        q: "Which LLM will my app use — Claude, GPT, or Gemini?",
        a: "Whichever fits the workload best. I'm model-agnostic. Most reasoning-heavy work goes to Claude; most structured extraction goes to GPT; some multimodal (vision, voice) workloads run cheaper on Gemini. The choice is made per project, not per contract.",
      },
    ],
    relatedProjectSlugs: ["ezscribe", "ezitemizer", "car-value-recovery"],
    ctaHeadline: "Ready to ship the AI app you've been sketching in Notion?",
    ctaBody:
      "Book a 45-minute scoping call. You leave with a clear scope, a clear price, a target delivery window, and a clear picture of the product you'll be looking at live in a few weeks.",
  },
];

export const getServiceBySlug = (slug) => services.find((s) => s.slug === slug);

export default services;
